import React, { useMemo, useState } from 'react';
import { KeyRound, Trash2, LogIn as LogInIcon, UserRoundPlus, Loader2, User, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLock } from '../../contexts/LockContext';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './ChangePasswordModal';
import DeleteAccountModal from './DeleteAccountModal';
import LanguageSelector from '../Common/LanguageSelector';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const rowClass =
  'flex items-center justify-between w-full p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors text-left';

const UserSettingsSection = () => {
  const { t } = useTranslation('settings');
  const { user, changePassword, deleteAccount } = useAuth();
  const { hasPin, setPin, removePin, checkPinStatus } = useLock();
  const navigate = useNavigate();
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passwordModalError, setPasswordModalError] = useState('');
  const [deleteModalError, setDeleteModalError] = useState('');
  const [isSubmittingPassword, setSubmittingPassword] = useState(false);
  const [isSubmittingDelete, setSubmittingDelete] = useState(false);

  const [pinChangeMode, setPinChangeMode] = useState(false);
  const [pinRemoveMode, setPinRemoveMode] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [isChangingPin, setIsChangingPin] = useState(false);

  const hasLocalPassword = useMemo(() => {
    if (!user) return false;
    if (typeof user.hasPassword === 'boolean') return user.hasPassword;
    const provider = String(user.provider || 'email').toLowerCase();
    return provider === 'email';
  }, [user]);

  const handlePasswordSubmit = async ({ currentPassword, newPassword }) => {
    if (!changePassword) return;
    setSubmittingPassword(true);
    setPasswordModalError('');
    try {
      const result = await changePassword(currentPassword, newPassword);
      if (!result?.success) {
        setPasswordModalError(result?.error || t('userSection.passwordUpdateFailed'));
        return;
      }
      setPasswordModalOpen(false);
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('userSection.passwordUpdatedToastTitle'),
              message: '',
            },
          })
        );
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordModalError(getApiErrorMessage(error, { fallback: t('userSection.passwordUpdateFailed') }));
    } finally {
      setSubmittingPassword(false);
    }
  };

  const handleDeleteSubmit = async ({ password }) => {
    if (!deleteAccount) return;
    setSubmittingDelete(true);
    setDeleteModalError('');
    try {
      const result = await deleteAccount(hasLocalPassword ? password : null);
      if (!result?.success) {
        setDeleteModalError(result?.error || t('userSection.accountDeleteFailed'));
        return;
      }
      setDeleteModalOpen(false);
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('userSection.accountDeletedToastTitle'),
              message: '',
            },
          })
        );
      }
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Account delete error:', error);
      setDeleteModalError(getApiErrorMessage(error, { fallback: t('userSection.accountDeleteFailedGeneric') }));
    } finally {
      setSubmittingDelete(false);
    }
  };

  const isGuest = useMemo(() => !user, [user]);

  const pinIdleButtons = (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => {
          setPinChangeMode(true);
          setPinRemoveMode(false);
          setPinError('');
        }}
        disabled={isChangingPin}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <KeyRound className="w-4 h-4" />
        {hasPin ? t('userSection.changePinButton') : t('userSection.setPinButton')}
      </button>
      {hasPin && (
        <button
          type="button"
          onClick={() => {
            setPinRemoveMode(true);
            setPinChangeMode(false);
            setCurrentPin('');
            setPinError('');
          }}
          disabled={isChangingPin}
          className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/40 text-red-500 hover:bg-red-500/10 rounded-md font-medium transition-colors disabled:opacity-60"
        >
          <Trash2 className="w-4 h-4" />
          {t('userSection.removePinButton')}
        </button>
      )}
    </div>
  );

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('userSection.title')}
      </h1>

      {isGuest ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-6 bg-white dark:bg-[var(--color-bg-base)] text-center">
            <p className="text-slate-700 dark:text-slate-200 mb-4">{t('userSection.guestMessage')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
              >
                <LogInIcon className="w-4 h-4" />
                {t('userSection.login')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md font-medium hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors"
              >
                <UserRoundPlus className="w-4 h-4" />
                {t('userSection.register')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Profile + Password + PIN — single flow, no section headers */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
            <button type="button" onClick={() => navigate('/profile')} className={rowClass}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-muted)] flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-200">{t('userSection.profileLabel')}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('userSection.profileDescription')}
                  </p>
                </div>
              </div>
            </button>

            {hasLocalPassword ? (
              <button type="button" onClick={() => setPasswordModalOpen(true)} className={rowClass}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-muted)] flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-200">
                      {t('userSection.changePasswordLabel')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('userSection.changePasswordDescription')}
                    </p>
                  </div>
                </div>
              </button>
            ) : (
              <div className={`${rowClass} hover:bg-white dark:hover:bg-[var(--color-bg-base)] cursor-default`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-muted)] flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-200">
                      {t('userSection.changePasswordLabel')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('userSection.googleOnlyPasswordHint')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                {hasPin ? t('userSection.appLockDescriptionHasPin') : t('userSection.appLockDescriptionNoPin')}
              </p>
              {!pinChangeMode && !pinRemoveMode ? (
                pinIdleButtons
              ) : pinRemoveMode ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('userSection.removePinDescription')}
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('userSection.currentPinLabel')}
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      autoComplete="off"
                      value={currentPin}
                      onChange={(e) => {
                        setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                        setPinError('');
                      }}
                      placeholder="••••"
                      className="w-full max-w-[120px] px-3 py-2 text-center text-lg tracking-widest rounded-md border border-slate-300 dark:border-slate-700 bg-transparent text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isChangingPin}
                    />
                  </div>
                  {pinError && <p className="text-sm text-red-500 dark:text-red-400">{pinError}</p>}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (currentPin.length !== 4) {
                          setPinError(t('userSection.currentPinLengthError'));
                          return;
                        }
                        setIsChangingPin(true);
                        setPinError('');
                        try {
                          const result = await removePin(currentPin);
                          if (result?.ok) {
                            setPinRemoveMode(false);
                            setCurrentPin('');
                            window.dispatchEvent(
                              new CustomEvent('notesplus-show-toast', {
                                detail: {
                                  type: 'success',
                                  title: t('userSection.pinRemovedToastTitle'),
                                  message: t('userSection.pinRemovedToastMessage'),
                                },
                              })
                            );
                            await checkPinStatus();
                          } else {
                            setPinError(
                              result?.error === 'CURRENT_PIN_INCORRECT'
                                ? t('userSection.currentPinIncorrectError')
                                : t('userSection.pinRemoveError')
                            );
                          }
                        } catch {
                          setPinError(t('userSection.pinRemoveError'));
                        } finally {
                          setIsChangingPin(false);
                        }
                      }}
                      disabled={isChangingPin || currentPin.length !== 4}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      {isChangingPin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      {isChangingPin ? t('userSection.saving') : t('userSection.removePinConfirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPinRemoveMode(false);
                        setCurrentPin('');
                        setPinError('');
                      }}
                      disabled={isChangingPin}
                      className="px-4 py-2 rounded-md text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                    >
                      {t('userSection.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {hasPin && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('userSection.currentPinLabel')}
                      </label>
                      <input
                        type="password"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        autoComplete="off"
                        value={currentPin}
                        onChange={(e) => {
                          setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                          setPinError('');
                        }}
                        placeholder="••••"
                        className="w-full max-w-[120px] px-3 py-2 text-center text-lg tracking-widest rounded-md border border-slate-300 dark:border-slate-700 bg-transparent text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        disabled={isChangingPin}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {hasPin ? t('userSection.newPinLabel') : t('userSection.pinLabel')}
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      autoComplete="off"
                      value={newPin}
                      onChange={(e) => {
                        setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                        setPinError('');
                      }}
                      placeholder="••••"
                      className="w-full max-w-[120px] px-3 py-2 text-center text-lg tracking-widest rounded-md border border-slate-300 dark:border-slate-700 bg-transparent text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isChangingPin}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {hasPin ? t('userSection.newPinConfirmLabel') : t('userSection.pinConfirmLabel')}
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      autoComplete="off"
                      value={confirmNewPin}
                      onChange={(e) => {
                        setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                        setPinError('');
                      }}
                      placeholder="••••"
                      className="w-full max-w-[120px] px-3 py-2 text-center text-lg tracking-widest rounded-md border border-slate-300 dark:border-slate-700 bg-transparent text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isChangingPin}
                    />
                  </div>
                  {pinError && <p className="text-sm text-red-500 dark:text-red-400">{pinError}</p>}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (hasPin && currentPin.length !== 4) {
                          setPinError(t('userSection.currentPinLengthError'));
                          return;
                        }
                        if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
                          setPinError(t('userSection.pinLengthError'));
                          return;
                        }
                        if (newPin !== confirmNewPin) {
                          setPinError(t('userSection.pinMismatchError'));
                          return;
                        }
                        setIsChangingPin(true);
                        setPinError('');
                        try {
                          const result = await setPin(newPin, hasPin ? { currentPin } : {});
                          if (result?.ok) {
                            setPinChangeMode(false);
                            setCurrentPin('');
                            setNewPin('');
                            setConfirmNewPin('');
                            window.dispatchEvent(
                              new CustomEvent('notesplus-show-toast', {
                                detail: {
                                  type: 'success',
                                  title: t('userSection.pinUpdatedToastTitle'),
                                  message: t('userSection.pinUpdatedToastMessage'),
                                },
                              })
                            );
                            await checkPinStatus();
                          } else if (result?.error === 'CURRENT_PIN_INCORRECT') {
                            setPinError(t('userSection.currentPinIncorrectError'));
                          } else if (result?.error === 'INVALID_PIN') {
                            setPinError(t('userSection.pinLengthError'));
                          } else {
                            setPinError(t('userSection.pinUpdateError'));
                          }
                        } catch (error) {
                          setPinError(t('userSection.pinUpdateError'));
                          console.error('Change PIN error:', error);
                        } finally {
                          setIsChangingPin(false);
                        }
                      }}
                      disabled={
                        isChangingPin ||
                        newPin.length !== 4 ||
                        confirmNewPin.length !== 4 ||
                        (hasPin && currentPin.length !== 4)
                      }
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isChangingPin ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                      {isChangingPin ? t('userSection.saving') : t('userSection.save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPinChangeMode(false);
                        setCurrentPin('');
                        setNewPin('');
                        setConfirmNewPin('');
                        setPinError('');
                      }}
                      disabled={isChangingPin}
                      className="px-4 py-2 rounded-md text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors disabled:opacity-60"
                    >
                      {t('userSection.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Language */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full p-4 gap-3 bg-white dark:bg-[var(--color-bg-base)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100/50 dark:bg-blue-500/10 flex items-center justify-center">
                  <Languages className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-200">{t('language.title')}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('language.description')}</p>
                </div>
              </div>
              <LanguageSelector variant="settings" />
            </div>
          </div>

          {/* Delete account — bottom, visually separated */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden mt-8">
            <button
              type="button"
              onClick={() => setDeleteModalOpen(true)}
              className="flex items-center justify-between w-full p-4 bg-white dark:bg-[var(--color-bg-base)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-200">
                    {t('userSection.deleteAccountLabel')}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t('userSection.deleteAccountDescription')}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </>
      )}

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSubmit={handlePasswordSubmit}
        isSubmitting={isSubmittingPassword}
        error={passwordModalError}
      />

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSubmit={handleDeleteSubmit}
        isSubmitting={isSubmittingDelete}
        error={deleteModalError}
        requirePassword={hasLocalPassword}
      />
    </>
  );
};

export default UserSettingsSection;
